import { Component, ViewChild, TemplateRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Countries } from '../../../enums/countries';
import { CustomerType } from '../../../enums/customer-type';
import { IWizard } from '../../../interfaces/iwizard';
import { WizardSteps } from '../../../interfaces/wizard-steps';
import { EnrollmentService } from '../../../services/enrollment.service';
import { GeneralService } from '../../../services/general.service';
import { CustomerService } from '../../../services/customer.service';
import { Customer } from '../../../interfaces/customer';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, Routes } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-e-company-info',
    templateUrl: './e-company-info.component.html',
    styleUrls: ['./e-company-info.component.scss'],
    standalone: false
})
export class ECompanyInfoComponent {
     countries = Countries;
     customerType = CustomerType;
     stateList: any = [];
     companyInfo!: FormGroup;
     isVerifying: boolean = false;
     existingCompanyName: string = ''; 
     existingCompanyAddress: string = '';
     isLoading = true;

     @ViewChild('PossibleDuplicateModal') possibleDuplicateModal: TemplateRef<any> | undefined;

     constructor (public generalService: GeneralService,
          public enrollmentService: EnrollmentService,
          public customerService: CustomerService,
          private router: Router,
          public modalService: NgbModal
     ) {
          const states = enrollmentService.getStates();
          states.subscribe((v: any) => this.loadStates(v));
     }

     wizardObj: IWizard = {
          steps: [
               {step:1, text:'Welcome'} as WizardSteps,
               {step:2, text:'Company Information'} as WizardSteps,
               {step:3, text:'User Accounts'} as WizardSteps,
               {step:4, text:'Review'} as WizardSteps,
               {step:5, text:'Contract Documents'} as WizardSteps,
               {step:6, text:'Submit'} as WizardSteps,
               {step:7, text:'Billing Information'} as WizardSteps
          ], 
          _cancelLinkRoute: '',
          backLinkRoute(currentPane: number) {;
            // not implimented
            return '';
          },
          continueLinkRoute(currentPane: number) {;
            // not implimented
            return '';
          },
          totalSteps: 1,
          type: 'enroll',
          visualProgressStep(currentPane: number) {;
            // not implimented
            return 1;
          }
     }

     ngOnInit(): void {
          const countryZipPattern = (this.enrollmentService.newCustomer?.country?.toString() === 'USA' ?
               /(^\d{5}(-\d{4})?$)/ : 
                /(^[ABCEGHJKLMNPRSTVXYabceghjklmnprstvxy]{1}\d{1}[A-Za-z]{1}\d{1}[A-Za-z]{1}\d{1}$)/
               )
          this.companyInfo = new FormGroup({
               addressLine1: new FormControl(this.enrollmentService.newCustomer!.address!.addressLine1, [
                    Validators.required,
                    Validators.maxLength(34),
                    Validators.pattern(/^([a-zA-Z0-9 '.\/]*)$/) 
               ]),
               addressLine2: new FormControl(this.enrollmentService.newCustomer!.address!.addressLine2, [
                    Validators.maxLength(14),
                    Validators.pattern(/^([a-zA-Z0-9 '.\/]*)$/) 
               ]),
               city: new FormControl(this.enrollmentService.newCustomer!.address!.city, [
                    Validators.required,
                    Validators.maxLength(30),
                    Validators.pattern(/^([a-zA-Z0-9 '.\/]*)$/) 
               ]),
               state: new FormControl(this.enrollmentService.newCustomer!.address!.state, [
                    Validators.required
               ]),
               zip: new FormControl(this.enrollmentService.newCustomer!.address!.zip, [
                    Validators.required,
                    Validators.pattern(countryZipPattern)
               ])
          });  
          
          
          setTimeout(() => this.scrollToTop(), 500);
          this.isLoading = false;
     }

     get addressLine1() { return this.companyInfo!.get('addressLine1'); }
     get addressLine2() { return this.companyInfo!.get('addressLine2'); };  
     get city() { return this.companyInfo!.get('city'); };  
     get state() { return this.companyInfo!.get('state'); };  
     get zip() { return this.companyInfo!.get('zip'); };  

     hasErrors(){
          return(this.addressLine1!.errors || this.addressLine2!.errors || this.city!.errors || this.state?.value === 0 || this.state!.errors || this.zip!.errors ? 'true': null)
     }

     loadStates(value: string[]) {
          const index = value.indexOf("PQ");
          if(index > -1){
              value[index] = "QC";
          }
          const index2 = value.indexOf("NF");
          if(index2 > -1){
              value[index2] = "NL";
          }
          this.stateList = value;
      }

     scrollToTop() {
          window.scrollTo(0, 0);
     }

     ngAfterViewInit(){
          window.onbeforeunload = function () {
               return 'You have created an account but not set up billing. Are you sure you wish to exit?';
          }
     }

     getTranslator(val: string): string {
          let translation: string = this.generalService.getTranslation(val,'base');
          if (translation === undefined) {
              translation = this.generalService.getTranslation(val,'base');
          }
          return translation;
      }

     openDocument(docName: string) {
          this.generalService.openDocument(docName);
     }

     ngOnDestroy(){
          this.enrollmentService.newCustomer!.address!.addressLine1 = this.companyInfo!.get('addressLine1')?.value;
          this.enrollmentService.newCustomer!.address!.addressLine2 = this.companyInfo!.get('addressLine2')?.value;
          this.enrollmentService.newCustomer!.address!.city = this.companyInfo!.get('city')?.value;
          this.enrollmentService.newCustomer!.address!.state = this.companyInfo!.get('state')?.value;
          this.enrollmentService.newCustomer!.address!.zip = this.companyInfo!.get('zip')?.value;
          this.enrollmentService.newCustomer!.city = this.companyInfo!.get('city')?.value;
          this.enrollmentService.newCustomer!.state = this.companyInfo!.get('state')?.value;
          this.enrollmentService.newCustomer!.zip = this.companyInfo!.get('zip')?.value;
          this.enrollmentService.saveToLocal();
     }

     checkForExistingCustomer() {
          this.scrollToTop();
          this.isVerifying = true;
          if (this.enrollmentService.newCustomer!.name) {
               const matchPromise = this.customerService.getExistingCustomer(
                    this.enrollmentService.newCustomer!.name!.trim(),
                    this.enrollmentService.newCustomer!.address!.country!.toString(),
                    this.companyInfo!.get('state')?.value);
               matchPromise.subscribe((response: Customer | any) => this.displayExistingCustomer(response));
          }
     }

     displayExistingCustomer(existingCustomer: Customer | any) {
          if (existingCustomer && existingCustomer.dbaName) {
               this.existingCompanyName = existingCustomer.dbaName;
               this.existingCompanyAddress = `${existingCustomer.street} ${existingCustomer.city}, ${existingCustomer.state} ${existingCustomer.zip} ${existingCustomer.country}`;
               
               let ngbModalOptions: NgbModalOptions = {
                    backdrop: 'static',
                    keyboard: false,
                    ariaLabelledBy: 'possibleDuplicateTitle'
               };
               this.modalService.open(this.possibleDuplicateModal, ngbModalOptions);
               this.isVerifying = false;
          } else {
               // No customer match found. 
               this.continueEnrollment();
          }
     }

     continueEnrollment() {
          this.isVerifying = false;
          this.modalService.dismissAll();
          const nextUrl = this.enrollmentService.newCustomer!.customerType === this.customerType.AH ? '/signup/8' : '/signup/7';
          this.router.navigateByUrl(nextUrl);
     }

     cancelDuplicate() {
          this.modalService.dismissAll();
          this.router.navigateByUrl('/');
     }
}
